/**
 * Chat Conversation PDF Export — Ministry of Justice Official Letterhead
 * v9: Field selector dialog + improved padding
 */

export const exportChatPDF = (submission, language = 'ar', themeSettings = {}) => {
    const isRTL = language === 'ar'
    const d = submission.data || {}
    const dir = isRTL ? 'rtl' : 'ltr'
    const lang = isRTL ? 'ar' : 'en'

    const primary = '#1a3a6b'
    const primaryLight = '#e8f2ef'
    const logoUrl = themeSettings?.logoUrl || '/moj-logo.png'
    const watermarkUrl = themeSettings?.logoUrl || '/moj-logo.png'

    const L = {
        title: isRTL ? 'سجل محادثة مباشرة' : 'Live Chat Transcript',
        chatInfo: isRTL ? 'معلومات المحادثة' : 'Chat Information',
        transcript: isRTL ? 'نص المحادثة' : 'Chat Transcript',
        noMessages: isRTL ? 'لا توجد رسائل' : 'No messages',
        time: isRTL ? 'الوقت' : 'Time',
        sender: isRTL ? 'المرسل' : 'Sender',
        message: isRTL ? 'الرسالة' : 'Message',
        selectFields: isRTL ? 'اختر الحقول للتصدير' : 'Select Export Fields',
        selectAll: isRTL ? 'تحديد الكل' : 'Select All',
        deselectAll: isRTL ? 'إلغاء تحديد الكل' : 'Deselect All',
        exportPdf: isRTL ? 'تصدير PDF' : 'Export PDF',
        cancel: isRTL ? 'إلغاء' : 'Cancel',
        statusOpen: isRTL ? 'مفتوح' : 'Open',
        statusClosed: isRTL ? 'مغلق' : 'Closed',
        statusActive: isRTL ? 'نشط' : 'Active',
    }

    const statusMap = { closed: L.statusClosed, active: L.statusActive }
    const statusText = statusMap[d.chat_status] || L.statusOpen

    // ── All available fields with their labels and values ──
    const allFields = [
        { key: 'visitor', label: isRTL ? 'اسم الزائر' : 'Visitor Name', value: d.chat_visitor_name || '—' },
        { key: 'email', label: isRTL ? 'البريد الإلكتروني' : 'Email', value: d.chat_visitor_email || '—' },
        { key: 'status', label: isRTL ? 'الحالة' : 'Status', value: statusText },
        { key: 'duration', label: isRTL ? 'المدة' : 'Duration', value: d.chat_duration_formatted || d.chat_duration || '—' },
        { key: 'totalMsgs', label: isRTL ? 'إجمالي الرسائل' : 'Total Messages', value: d.chat_total_messages ?? '0' },
        { key: 'visitorMsgs', label: isRTL ? 'رسائل الزائر' : 'Visitor Messages', value: d.chat_visitor_messages ?? '0' },
        { key: 'agentMsgs', label: isRTL ? 'رسائل الوكيل' : 'Agent Messages', value: d.chat_agent_messages ?? '0' },
        { key: 'attachments', label: isRTL ? 'المرفقات' : 'Attachments', value: d.chat_attachment_count ?? '0' },
        { key: 'agents', label: isRTL ? 'الوكلاء' : 'Agents', value: d.chat_agents || '—' },
        { key: 'started', label: isRTL ? 'بداية المحادثة' : 'Started At', value: submission.created_at || '—' },
        { key: 'closed', label: isRTL ? 'إغلاق المحادثة' : 'Closed At', value: d.chat_closed_at || '—' },
        { key: 'closedBy', label: isRTL ? 'أغلق بواسطة' : 'Closed By', value: d.chat_closed_by || '—' },
    ]

    // ── Show Field Selector Dialog ──
    _showFieldSelector(allFields, L, dir, lang, isRTL, primary, primaryLight, (selectedKeys) => {
        const selectedFields = allFields.filter(f => selectedKeys.includes(f.key))
        _generatePDF(submission, d, selectedFields, language, L, dir, lang, isRTL, primary, primaryLight, logoUrl, watermarkUrl)
    })
}

function _showFieldSelector(fields, L, dir, lang, isRTL, primary, primaryLight, onExport) {
    const al = isRTL ? 'right' : 'left'

    const checkboxesHtml = fields.map(f => `
        <label class="field-item" data-key="${f.key}">
            <input type="checkbox" value="${f.key}" checked>
            <span class="field-label">${f.label}</span>
            <span class="field-val">${f.value}</span>
        </label>
    `).join('')

    const html = `<!DOCTYPE html>
<html dir="${dir}" lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${L.selectFields}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
    font-family: 'Cairo', sans-serif; background: #f0f4f3; direction: ${dir};
    display: flex; justify-content: center; align-items: center; min-height: 100vh;
}
.dialog {
    background: #fff; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.15);
    width: 520px; max-width: 95vw; overflow: hidden;
}
.dialog-head {
    background: ${primary}; color: #fff; padding: 16px 24px;
    font-size: 16px; font-weight: 700; text-align: center;
}
.dialog-body { padding: 16px 24px; }
.toolbar {
    display: flex; gap: 8px; margin-bottom: 12px;
}
.toolbar button {
    padding: 6px 14px; border-radius: 6px; border: 1.5px solid ${primary};
    font-family: 'Cairo', sans-serif; font-size: 12px; font-weight: 600;
    cursor: pointer; background: #fff; color: ${primary}; transition: all 0.2s;
}
.toolbar button:hover { background: ${primaryLight}; }

.fields-list {
    display: flex; flex-direction: column; gap: 6px;
    max-height: 360px; overflow-y: auto; padding: 4px 0;
}
.field-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 14px; border-radius: 8px; border: 1.5px solid #e2e8f0;
    cursor: pointer; transition: all 0.2s; background: #fafcfb;
}
.field-item:hover { border-color: ${primary}; background: ${primaryLight}; }
.field-item input[type="checkbox"] {
    width: 18px; height: 18px; accent-color: ${primary}; cursor: pointer;
    flex-shrink: 0;
}
.field-label { font-size: 13px; font-weight: 600; color: #333; flex: 1; }
.field-val { font-size: 12px; color: #718096; max-width: 160px; text-align: ${isRTL ? 'left' : 'right'}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.dialog-footer {
    display: flex; gap: 10px; justify-content: center;
    padding: 16px 24px; border-top: 1px solid #e2e8f0; background: #fafcfb;
}
.btn {
    padding: 10px 28px; border-radius: 8px; font-family: 'Cairo', sans-serif;
    font-size: 14px; font-weight: 700; cursor: pointer; border: none; transition: all 0.2s;
}
.btn-export { background: ${primary}; color: #fff; }
.btn-export:hover { background: #256b56; }
.btn-cancel { background: #e2e8f0; color: #4a5568; }
.btn-cancel:hover { background: #cbd5e0; }
</style>
</head>
<body>
<div class="dialog">
    <div class="dialog-head">${L.selectFields}</div>
    <div class="dialog-body">
        <div class="toolbar">
            <button id="selAll">${L.selectAll}</button>
            <button id="deselAll">${L.deselectAll}</button>
        </div>
        <div class="fields-list">${checkboxesHtml}</div>
    </div>
    <div class="dialog-footer">
        <button class="btn btn-export" id="btnExport">${L.exportPdf}</button>
        <button class="btn btn-cancel" id="btnCancel">${L.cancel}</button>
    </div>
</div>

<script>
document.getElementById('selAll').onclick = () => {
    document.querySelectorAll('.field-item input').forEach(c => c.checked = true);
};
document.getElementById('deselAll').onclick = () => {
    document.querySelectorAll('.field-item input').forEach(c => c.checked = false);
};
document.getElementById('btnCancel').onclick = () => window.close();
document.getElementById('btnExport').onclick = () => {
    const selected = [];
    document.querySelectorAll('.field-item input:checked').forEach(c => selected.push(c.value));
    if (window.onFieldsSelected) window.onFieldsSelected(selected);
};
</script>
</body>
</html>`

    const w = window.open('', '_blank', 'width=580,height=640')
    if (!w) { alert(isRTL ? 'يرجى السماح بالنوافذ المنبثقة' : 'Please allow pop-ups'); return }
    w.document.write(html)
    w.document.close()

    w.onFieldsSelected = (selectedKeys) => {
        w.close()
        onExport(selectedKeys)
    }
}

function _generatePDF(submission, d, selectedFields, language, L, dir, lang, isRTL, primary, primaryLight, logoUrl, watermarkUrl) {
    const al = isRTL ? 'right' : 'left'

    // ── Info cards HTML ──
    const cardsHtml = selectedFields.map(f =>
        `<div class="card"><div class="ck">${f.label}</div><div class="cv">${f.value}</div></div>`
    ).join('')

    const hasCards = selectedFields.length > 0

    // ── Notes ──
    const notesLabel = isRTL ? 'ملاحظات الوكيل' : 'Agent Notes'
    const notesHtml = d.chat_notes ? `<div class="notes"><b>${notesLabel}:</b> ${d.chat_notes}</div>` : ''

    // ── Rating ──
    const ratingLabel = isRTL ? 'تقييم الزائر' : 'Rating'
    const ratingHtml = d.chat_rating > 0
        ? `<div class="rate">${ratingLabel}: <span class="stars">${'★'.repeat(d.chat_rating)}${'☆'.repeat(5 - d.chat_rating)}</span></div>`
        : ''

    // ── Transcript: pre-group continuation lines into parent messages ──
    const rawLines = (d.chat_transcript || '').split('\n');
    const groupedMessages = [];
    for (let li = 0; li < rawLines.length; li++) {
        const line = rawLines[li];
        if (!line.trim()) continue;
        const m = line.match(/^\[([^\]]+)\]\s+(.+?):\s(.*)$/);
        if (m) {
            groupedMessages.push({ time: m[1], who: m[2], text: m[3] });
        } else if (groupedMessages.length > 0) {
            groupedMessages[groupedMessages.length - 1].text += '\n' + line;
        }
    }
    const chatRowsHtml = groupedMessages.length > 0 ? groupedMessages.map(msg => {
        const isAgent = msg.who !== 'Visitor';
        const cls = isAgent ? 'agent' : 'visitor';
        const isArabic = /[\u0600-\u06FF]/.test(msg.text);
        const msgStyle = isArabic ? ' style="direction:rtl;text-align:right;unicode-bidi:embed"' : '';
        const escapedText = msg.text.replace(/\n/g, '<br>');
        return `<tr class="${cls}"><td class="t-time">${msg.time}</td><td class="t-who">${msg.who}</td><td class="t-msg"${msgStyle}>${escapedText}</td></tr>`
    }).join('') : `<tr><td colspan="3" class="no-msg">${L.noMessages}</td></tr>`

    // ── Determine grid columns based on selection count ──
    const cols = selectedFields.length <= 4 ? 2 : 3

    const html = `<!DOCTYPE html>
<html dir="${dir}" lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${submission.submission_id} - ${L.title}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

@page {
    size: A4;
    margin: 12mm 15mm 20mm 15mm;
}

body {
    font-family: 'Cairo', 'Segoe UI', sans-serif;
    font-size: 11pt;
    color: #2d3748;
    background: #fff;
    direction: ${dir};
    line-height: 1.5;
}

/* Watermark */
.wm { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); opacity: 0.05; z-index: 0; pointer-events: none; }
.wm img { width: 420px; }
.wrap { position: relative; z-index: 1; width: 100%; padding: 0 20pt; }

/* ===== HEADER ===== */
.hdr {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10pt 0; border-bottom: 2.5pt solid ${primary}; margin-bottom: 10pt;
}
.hdr-s { flex: 1; text-align: center; font-size: 12pt; font-weight: 600; color: #333; }
.hdr-c { flex: 0 0 auto; text-align: center; }
.hdr-c img { height: 65pt; }

/* ===== DOC BAR ===== */
.doc {
    text-align: center; padding: 8pt 20pt; margin-bottom: 12pt;
    background: ${primaryLight}; border: 1.5pt solid ${primary}; border-radius: 8pt;
}
.doc h1 { font-size: 14pt; font-weight: 700; color: #1a1a1a; letter-spacing: 0.5pt; }
.doc p { font-size: 10pt; color: ${primary}; font-weight: 600; margin-top: 2pt; }

/* ===== SECTION TITLES ===== */
.st {
    font-size: 12pt; font-weight: 700; color: ${primary};
    margin: 10pt 0 6pt; padding-bottom: 3pt;
    border-bottom: 1.5pt solid ${primaryLight}; text-align: ${al};
}

/* ===== INFO CARDS ===== */
.cg { display: grid; grid-template-columns: repeat(${cols}, 1fr); gap: 6pt; margin-bottom: 8pt; }
.card {
    background: #fafcfb; border: 1pt solid #e2e8f0;
    border-${al}: 3pt solid ${primary}; border-radius: 5pt;
    padding: 6pt 10pt; text-align: ${al};
}
.ck { font-size: 8pt; font-weight: 600; color: #718096; text-transform: uppercase; letter-spacing: 0.3pt; margin-bottom: 1pt; }
.cv { font-size: 11pt; font-weight: 700; color: #1a202c; word-break: break-word; }

/* ===== NOTES ===== */
.notes {
    background: #fffff0; border: 1pt solid #ecc94b; border-radius: 5pt;
    padding: 6pt 10pt; font-size: 10pt; color: #744210; margin-bottom: 6pt;
}

/* ===== RATING ===== */
.rate { text-align: center; font-size: 10pt; color: #718096; margin-bottom: 8pt; }
.stars { font-size: 15pt; color: #ed8936; letter-spacing: 2pt; }

/* ===== CHAT TABLE ===== */
.chat-tbl {
    width: 100%; border-collapse: collapse;
    font-size: 10pt; margin-top: 3pt;
}
.chat-tbl thead th {
    background: ${primary}; color: #fff;
    font-size: 9pt; font-weight: 600; text-transform: uppercase;
    padding: 5pt 8pt; text-align: ${al};
    letter-spacing: 0.3pt;
}
.chat-tbl thead th:first-child { border-radius: ${isRTL ? '0 6pt 0 0' : '6pt 0 0 0'}; }
.chat-tbl thead th:last-child { border-radius: ${isRTL ? '6pt 0 0 0' : '0 6pt 0 0'}; }

.chat-tbl tbody tr { border-bottom: 0.5pt solid #e8e8e8; }
.chat-tbl tbody tr:nth-child(even) { background: #fafcfb; }
.chat-tbl tbody tr.agent { background: ${primaryLight}; }
.chat-tbl tbody tr.agent:nth-child(even) { background: #dceee8; }

.chat-tbl td { padding: 5pt 8pt; vertical-align: top; }
.t-time { width: 18%; font-size: 9pt; color: #718096; white-space: nowrap; }
.t-who { width: 14%; font-size: 9.5pt; font-weight: 700; white-space: nowrap; }
.agent .t-who { color: ${primary}; }
.visitor .t-who { color: #4a5568; }
.t-msg { font-size: 10pt; color: #2d3748; word-break: break-word; line-height: 1.4; }

.sys-row td { text-align: center; font-size: 9pt; color: #a0aec0; font-style: italic; padding: 3pt 8pt; }
.no-msg { text-align: center; padding: 14pt; color: #a0aec0; font-style: italic; }

/* ===== FOOTER ===== */
.ftr {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: #fff; border-top: 1.5pt solid ${primary}; padding-top: 4pt;
}
.ftr-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 3pt 15pt; font-size: 7.5pt; color: #718096; line-height: 1.4;
}
.ftr-bar { height: 4pt; background: ${primary}; margin-top: 3pt; }

/* ===== PRINT ===== */
@media print {
    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .card { background: #fafcfb !important; border-${al}: 3pt solid ${primary} !important; }
    .chat-tbl thead th { background: ${primary} !important; color: #fff !important; }
    .chat-tbl tbody tr.agent { background: ${primaryLight} !important; }
    .chat-tbl tbody tr:nth-child(even) { background: #fafcfb !important; }
    .chat-tbl tbody tr.agent:nth-child(even) { background: #dceee8 !important; }
    .ftr { position: fixed; bottom: 0; }
}
</style>
</head>
<body>

<div class="wm"><img src="${watermarkUrl}" alt=""></div>

<div class="wrap">

    <div class="hdr">
        <div class="hdr-s">Syrian Arab Republic</div>
        <div class="hdr-c"><img src="${logoUrl}" alt="Ministry of Justice"></div>
        <div class="hdr-s">الجمهورية العربية السورية</div>
    </div>

    <div class="doc">
        <h1>${submission.submission_id}</h1>
        <p>${L.title}</p>
    </div>

    ${hasCards ? `
    <div class="st">${L.chatInfo}</div>
    <div class="cg">${cardsHtml}</div>
    ` : ''}
    ${notesHtml}
    ${ratingHtml}

    <div class="st" style="page-break-before: always; break-before: page;">${L.transcript}</div>
    <table class="chat-tbl">
        <thead>
            <tr>
                <th>${L.time}</th>
                <th>${L.sender}</th>
                <th>${L.message}</th>
            </tr>
        </thead>
        <tbody>
            ${chatRowsHtml}
        </tbody>
    </table>

    <div class="ftr">
        <div class="ftr-row">
            <div style="text-align:left">Tel: +963115400800<br>Fax: +963115400700</div>
            <div style="text-align:center">www.moj.gov.sy<br>info@moj.gov.sy</div>
            <div style="text-align:right">دمشق، سورية<br>P.O.BOX: 6257 Damascus</div>
        </div>
        <div class="ftr-bar"></div>
    </div>

</div>
</body>
</html>`

    const w = window.open('', '_blank', 'width=900,height=1100')
    if (!w) { alert(isRTL ? 'يرجى السماح بالنوافذ المنبثقة' : 'Please allow pop-ups'); return }
    w.document.write(html)
    w.document.close()
    w.onload = () => setTimeout(() => { w.focus(); w.print() }, 500)
}

export default exportChatPDF
